import * as d3 from 'd3'
import countriesTopo from 'world-atlas/countries-110m.json'
import { feature, mesh } from 'topojson-client'
import { getViewportFeature } from './pointMapViewport'
import { BRAND_CONFIG } from '../../branding/brandConfig'

const THUMBNAIL = "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='320' height='160' viewBox='0 0 320 160'%3e%3crect width='320' height='160' fill='white'/%3e%3cellipse cx='160' cy='80' rx='122' ry='54' fill='none' stroke='%23c7cbd1' stroke-width='2'/%3e%3cpath d='M57 78c28-22 67-31 111-31 51 0 94 13 121 37' fill='none' stroke='%23d9dde2' stroke-width='2'/%3e%3cpath d='M62 101c31-18 67-27 106-27 46 0 90 11 125 30' fill='none' stroke='%23d9dde2' stroke-width='2'/%3e%3cpath d='M111 31c8 17 12 33 12 49 0 20-5 39-13 57' fill='none' stroke='%23d9dde2' stroke-width='2'/%3e%3cpath d='M184 29c-7 17-11 34-11 51 0 19 4 37 12 55' fill='none' stroke='%23d9dde2' stroke-width='2'/%3e%3ccircle cx='88' cy='69' r='7' fill='%234bc06a'/%3e%3ccircle cx='152' cy='95' r='9' fill='%232f80ed' opacity='.85'/%3e%3ccircle cx='220' cy='63' r='6' fill='%23ff8a5b'/%3e%3c/svg%3e"

const ICON = "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='56' height='56' viewBox='0 0 56 56'%3e%3cellipse cx='28' cy='28' rx='20' ry='14' fill='none' stroke='%2306c26c' stroke-width='2'/%3e%3cpath d='M9 28h38M28 14c4 4 6 9 6 14s-2 10-6 14M28 14c-4 4-6 9-6 14s2 10 6 14' fill='none' stroke='%2395e5c0' stroke-width='2'/%3e%3ccircle cx='20' cy='22' r='3' fill='%2306c26c'/%3e%3ccircle cx='33' cy='31' r='4' fill='%2395e5c0'/%3e%3c/svg%3e"

const PROJECTIONS = {
  'Natural Earth': () => d3.geoNaturalEarth1(),
  Mercator: () => d3.geoMercator(),
  'Equal Earth': () => d3.geoEqualEarth(),
}

const LAND_FEATURE = feature(countriesTopo, countriesTopo.objects.land)
const COUNTRY_BORDERS = mesh(
  countriesTopo,
  countriesTopo.objects.countries,
  (a, b) => a !== b
)

function getProjection(name) {
  return (PROJECTIONS[name] || PROJECTIONS['Natural Earth'])()
}

function isValidCoordinate(value, min, max) {
  return typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max
}

function getNumericExtent(values) {
  const validValues = values.filter((value) => typeof value === 'number' && Number.isFinite(value))
  return validValues.length ? d3.extent(validValues) : null
}

function appendNamedGroup(parent, id, label) {
  return parent
    .append('g')
    .attr('id', id)
    .attr('data-name', label)
}

function mapData(data, mapping) {
  const latitudeColumn = mapping.latitude?.value
  const longitudeColumn = mapping.longitude?.value
  const sizeColumn = mapping.size?.value
  const colorColumn = mapping.color?.value
  const labelColumn = mapping.label?.value

  if (!latitudeColumn || !longitudeColumn) return []

  return data
    .map((row) => ({
      ...row,
      __pointMapLatitude: Number(row[latitudeColumn]),
      __pointMapLongitude: Number(row[longitudeColumn]),
      __pointMapSize: sizeColumn ? Number(row[sizeColumn]) : null,
      __pointMapColor: colorColumn ? row[colorColumn] : null,
      __pointMapLabel: labelColumn ? row[labelColumn] : null,
    }))
    .filter(
      (row) =>
        isValidCoordinate(row.__pointMapLatitude, -90, 90) &&
        isValidCoordinate(row.__pointMapLongitude, -180, 180)
    )
}

function render(svgNode, data, visualOptions, mapping) {
  const {
    width,
    height,
    background,
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    projection: projectionName,
    viewMode,
    pointRadius,
    minPointRadius,
    maxPointRadius,
    pointOpacity,
    pointColor,
    pointStroke,
    pointStrokeWidth,
    showLabels,
    labelFontSize,
    showGraticule,
    landFill,
    countryBorderColor,
    countryBorderWidth,
    colorScale,
  } = visualOptions

  const svg = d3.select(svgNode)
  svg.selectAll('*').remove()

  svg
    .attr('width', width)
    .attr('height', height)
    .attr('viewBox', `0 0 ${width} ${height}`)

  svg.append('rect').attr('width', width).attr('height', height).attr('fill', background)

  const chartWidth = Math.max(0, width - marginLeft - marginRight)
  const chartHeight = Math.max(0, height - marginTop - marginBottom)

  const root = svg
    .append('g')
    .attr('id', 'point-map-chart')
    .attr('data-name', 'Point Map Chart')
    .attr('transform', `translate(${marginLeft},${marginTop})`)

  const projection = getProjection(projectionName)
  const viewport = [
    [0, 0],
    [chartWidth, chartHeight],
  ]

  projection.fitExtent(viewport, getViewportFeature(viewMode, data, visualOptions))

  const path = d3.geoPath(projection)

  const landGroup = appendNamedGroup(root, 'point-map-land-group', 'Land Group')
  const countryLinesGroup = appendNamedGroup(
    root,
    'point-map-country-lines',
    'Country Lines'
  )

  landGroup
    .append('path')
    .datum(LAND_FEATURE)
    .attr('id', 'point-map-land')
    .attr('data-name', 'Land')
    .attr('d', path)
    .attr('fill', landFill)
    .attr('stroke', '#d8e1db')
    .attr('stroke-width', 0.75)

  countryLinesGroup
    .append('path')
    .datum(COUNTRY_BORDERS)
    .attr('id', 'point-map-country-borders')
    .attr('data-name', 'Country Borders')
    .attr('d', path)
    .attr('fill', 'none')
    .attr('stroke', countryBorderColor)
    .attr('stroke-width', countryBorderWidth)
    .attr('stroke-linejoin', 'round')

  if (showGraticule) {
    appendNamedGroup(root, 'point-map-graticule-group', 'Graticule Group')
      .append('path')
      .datum(d3.geoGraticule10())
      .attr('id', 'point-map-graticule')
      .attr('data-name', 'Graticule')
      .attr('d', path)
      .attr('fill', 'none')
      .attr('stroke', '#e2e7ed')
      .attr('stroke-width', 0.7)
      .attr('opacity', 0.8)
  }

  if (!data.length) {
    appendNamedGroup(root, 'point-map-empty-state-group', 'Empty State')
      .append('text')
      .attr('id', 'point-map-empty-state')
      .attr('data-name', 'Empty State Message')
      .attr('x', chartWidth / 2)
      .attr('y', chartHeight / 2)
      .attr('text-anchor', 'middle')
      .attr('fill', '#666')
      .text('No valid latitude/longitude rows to plot')
    return
  }

  const sizeExtent = getNumericExtent(data.map((point) => point.__pointMapSize))
  const radiusScale =
    sizeExtent && sizeExtent[0] !== sizeExtent[1]
      ? d3.scaleSqrt().domain(sizeExtent).range([minPointRadius, maxPointRadius])
      : null

  const points = data
    .map((point) => {
      const coordinates = projection([
        point.__pointMapLongitude,
        point.__pointMapLatitude,
      ])
      return coordinates
        ? {
            ...point,
            __projectedX: coordinates[0],
            __projectedY: coordinates[1],
          }
        : null
    })
    .filter(Boolean)

  const pointLayer = appendNamedGroup(root, 'point-map-point-group', 'Point Group')

  pointLayer
    .selectAll('circle')
    .data(points)
    .join('circle')
    .attr('cx', (d) => d.__projectedX)
    .attr('cy', (d) => d.__projectedY)
    .attr('r', (d) =>
      radiusScale && typeof d.__pointMapSize === 'number'
        ? radiusScale(d.__pointMapSize)
        : pointRadius
    )
    .attr('fill', (d) =>
      mapping.color?.value ? colorScale(d.__pointMapColor) : pointColor
    )
    .attr('data-name', 'Point')
    .attr('stroke', pointStroke)
    .attr('stroke-width', pointStrokeWidth)
    .attr('opacity', pointOpacity)

  if (showLabels && mapping.label?.value) {
    appendNamedGroup(root, 'point-map-label-group', 'Label Group')
      .selectAll('text')
      .data(points.filter((point) => point.__pointMapLabel))
      .join('text')
      .attr('x', (d) => d.__projectedX + 6)
      .attr('y', (d) => d.__projectedY - 6)
      .attr('font-size', labelFontSize)
      .attr('fill', '#2d3748')
      .attr('data-name', 'Point Label')
      .text((d) => d.__pointMapLabel)
  }
}

const pointMap = {
  metadata: {
    name: 'Point map',
    id: 'rawgraphs.pointmap',
    thumbnail: THUMBNAIL,
    icon: ICON,
    categories: ['maps'],
    description:
      'It plots latitude and longitude values as points on a projected world map.',
    code: '',
    tutorial: '',
  },
  dimensions: [
    {
      id: 'latitude',
      name: 'Latitude',
      validTypes: ['number'],
      required: true,
      operation: 'get',
    },
    {
      id: 'longitude',
      name: 'Longitude',
      validTypes: ['number'],
      required: true,
      operation: 'get',
    },
    {
      id: 'size',
      name: 'Size',
      validTypes: ['number'],
      required: false,
      operation: 'get',
    },
    {
      id: 'color',
      name: 'Color',
      validTypes: ['number', 'string', 'date'],
      required: false,
      operation: 'get',
    },
    {
      id: 'label',
      name: 'Label',
      validTypes: ['number', 'string', 'date'],
      required: false,
      operation: 'get',
    },
  ],
  mapData,
  render,
  visualOptions: {
    marginTop: { type: 'number', label: 'Margin (top)', default: 18, group: 'artboard' },
    marginRight: { type: 'number', label: 'Margin (right)', default: 18, group: 'artboard' },
    marginBottom: { type: 'number', label: 'Margin (bottom)', default: 18, group: 'artboard' },
    marginLeft: { type: 'number', label: 'Margin (left)', default: 18, group: 'artboard' },
    projection: {
      type: 'text',
      label: 'Projection',
      options: ['Natural Earth', 'Mercator', 'Equal Earth'],
      default: 'Natural Earth',
      group: 'layout',
    },
    showGraticule: {
      type: 'boolean',
      label: 'Show graticule',
      default: false,
      group: 'layout',
    },
    viewMode: {
      type: 'text',
      label: 'Map view',
      options: ['World', 'Fit to data', 'Custom bounds'],
      default: 'Fit to data',
      group: 'layout',
    },
    minLatitude: {
      type: 'number',
      label: 'Min latitude',
      default: -90,
      min: -90,
      max: 90,
      group: 'layout',
    },
    maxLatitude: {
      type: 'number',
      label: 'Max latitude',
      default: 90,
      min: -90,
      max: 90,
      group: 'layout',
    },
    minLongitude: {
      type: 'number',
      label: 'Min longitude',
      default: -180,
      min: -180,
      max: 180,
      group: 'layout',
    },
    maxLongitude: {
      type: 'number',
      label: 'Max longitude',
      default: 180,
      min: -180,
      max: 180,
      group: 'layout',
    },
    pointRadius: {
      type: 'number',
      label: 'Point radius',
      default: 5,
      min: 1,
      group: 'points',
    },
    minPointRadius: {
      type: 'number',
      label: 'Minimum bubble radius',
      default: 4,
      min: 1,
      group: 'points',
    },
    maxPointRadius: {
      type: 'number',
      label: 'Maximum bubble radius',
      default: 12,
      min: 2,
      group: 'points',
    },
    pointOpacity: {
      type: 'number',
      label: 'Point opacity',
      default: 0.85,
      min: 0.1,
      max: 1,
      step: 0.05,
      group: 'points',
    },
    pointColor: {
      type: 'color',
      label: 'Point color',
      default: BRAND_CONFIG.ui.accentColor,
      group: 'colors',
    },
    pointStroke: {
      type: 'color',
      label: 'Point outline',
      default: '#ffffff',
      group: 'colors',
    },
    pointStrokeWidth: {
      type: 'number',
      label: 'Point outline width',
      default: 1,
      min: 0,
      group: 'colors',
    },
    landFill: {
      type: 'color',
      label: 'Land fill',
      default: '#eef3ea',
      group: 'colors',
    },
    countryBorderColor: {
      type: 'color',
      label: 'Country borders',
      default: '#c6d0da',
      group: 'colors',
    },
    countryBorderWidth: {
      type: 'number',
      label: 'Country border width',
      default: 0.7,
      min: 0,
      step: 0.1,
      group: 'colors',
    },
    showLabels: {
      type: 'boolean',
      label: 'Show labels',
      default: false,
      group: 'labels',
    },
    labelFontSize: {
      type: 'number',
      label: 'Label font size',
      default: 10,
      min: 8,
      group: 'labels',
      disabled: { showLabels: false },
    },
    colorScale: {
      type: 'colorScale',
      label: 'Color scale',
      dimension: 'color',
      default: { scaleType: 'ordinal', interpolator: 'schemeCategory10' },
      group: 'color',
    },
  },
  styles: {
    labelPrimary: {
      fontFamily: 'Arial, sans-serif',
      fontSize: '10px',
      fill: '#2d3748',
    },
  },
}

export default pointMap
